/* eslint-disable react/no-this-in-sfc */
/**
 * Default Popup options.
 */

import { ANALYTICS_ACTIONS, EVENT_NAMES } from '@/analytics/constants';
import { trackHelper } from '@/analytics/utils';

function getPopupDimension(varVal: number, minVal: number, maxVal: number): number {
  return Math.min(Math.max(Math.floor(varVal), minVal), maxVal);
}

type PopupArgs = {
  src: string;
  name: string;
  onMessageCallback?: (message: any) => void;
  onCancel?: () => void;
};

export interface CustomPopupWindow extends Window {
  onComplete?: (msg: string) => void;
}

/**
 * utility function to open a browswer popup when we need to redirect to a 3rd party service
 * @param {PopupArgs}
 * @returns {CustomPopupWindow | null}
 */
function createPopup({
  src,
  name,
  onMessageCallback,
  onCancel,
}: PopupArgs): CustomPopupWindow | null {
  const width = window.innerWidth || document.documentElement.clientWidth;
  const height = window.innerHeight || document.documentElement.clientHeight;

  const opts: optsType = {
    width: getPopupDimension(width * 0.7, 895, 1440),
    height: getPopupDimension(height * 0.8, 520, 1060),
    menubar: 'no',
    resizable: 'yes',
    location: 'no',
    scrollbars: 'yes',
  };

  type optsType = {
    width: number;
    height: number;
    menubar: string;
    resizable: string;
    scrollbars: string;
    location: string;
  };

  // turn the "opts" object into a window.open()-compatible String
  const opsArr: string[] = [];

  Object.keys(opts).forEach((key: string): void => {
    opsArr.push(`${key}=${opts[key as keyof typeof opts]}`);
  });

  const optsStr = opsArr.join(',');

  let popupWindow: CustomPopupWindow | null = null;

  try {
    popupWindow = window.open(src, name || '', optsStr); // might be null in IE9 if protected mode is turned on
  } catch (e) {
    // catch popup not open error
    trackHelper({
      objectName: EVENT_NAMES.POPUP_OPEN_FAILED,
      actionName: ANALYTICS_ACTIONS.ERROR,
      properties: {
        error: e,
      },
    });
  }

  if (!popupWindow) {
    return null;
  }

  try {
    // attach an event listener to the event triggered from the popup window
    // handle any callback based on the event from the react component
    // Note: onComplete is the event name triggered from the popup window will rename if needed
    popupWindow.onComplete = onMessageCallback;
    // autofocus the popup window when opened
    popupWindow.focus();

    // Define method to handle popup messages
    const handlePopupMessage = (message: any): void => {
      // Call the provided callback with the message
      if (onMessageCallback) {
        onMessageCallback(message);
      }
    };

    // Add event listener for messages from the popup
    const handleMessageFromPopup = (event: MessageEvent): void => {
      if (event.source === popupWindow) {
        handlePopupMessage(event.data);
        // close the popup when the event is received
        popupWindow?.close();
      }
    };

    window.addEventListener('message', handleMessageFromPopup);

    // Clean up event listener when the popup is closed
    popupWindow.addEventListener('beforeunload', () => {
      onCancel?.();
      window.removeEventListener('message', handleMessageFromPopup);
    });

    window.addEventListener('beforeunload', () => {
      if (popupWindow) {
        popupWindow.close();
      }
    });

    return popupWindow;
  } catch (err) {
    trackHelper({
      objectName: EVENT_NAMES.POPUP_RENDER_FAILED,
      actionName: ANALYTICS_ACTIONS.ERROR,
      properties: {
        error: err,
      },
    });
    return null;
  }
}

export default createPopup;
